import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { createPortal } from "react-dom";
import Navbar from "../components/Navbar";

import HelpCenter from "../components/HelpCenter";
import {
  getParanoidiSettings,
  fetchParanoidiSettings,
  saveParanoidiSettings,
} from "../data/paranoidiSettings";

function getApiBaseUrl() {
  return (
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api")
  ).replace(/\/$/, "");
}

function getPerformerSlug() {
  try {
    const performer = JSON.parse(
      window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
    );

    const slug = String(performer?.slug || "").trim();

    if (slug) {
      return slug;
    }
  } catch {
    // Käytetään fallbackia alla.
  }

  return "demo";
}

function getCurrentPerformer() {
  try {
    return JSON.parse(
      window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
    );
  } catch {
    return {};
  }
}

function getHeroImageUrl(settings) {
  return settings?.heroImageUrl || settings?.hero_image_url || "";
}

function addCacheBuster(url, version) {
  if (!url) {
    return "";
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${version}`;
}

function resolveAssetUrl(url) {
  const rawUrl = String(url || "").trim();

  if (!rawUrl) {
    return "";
  }

  if (
    rawUrl.startsWith("http://") ||
    rawUrl.startsWith("https://") ||
    rawUrl.startsWith("data:") ||
    rawUrl.startsWith("blob:")
  ) {
    return rawUrl;
  }

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (isLocalhost && rawUrl.startsWith("/backend/")) {
    return `http://localhost:4000${rawUrl}`;
  }

  if (isLocalhost && rawUrl.startsWith("/uploads/")) {
    return `http://localhost:4000${rawUrl}`;
  }

  return rawUrl;
}

function normalizeSponsorList(value) {
  const items = Array.isArray(value) ? value : [];

  return items
    .slice(0, 5)
    .map((item, index) => ({
      label: String(item?.label || item?.sponsorLabel || item?.sponsor_label || "").trim().slice(0, 80),
      name: String(item?.name || "").trim().slice(0, 80),
      offerText: String(item?.offerText || item?.offer_text || item?.description || "").trim().slice(0, 220),
      logoUrl: String(item?.logoUrl || item?.logo_url || item?.logo || "").trim(),
      linkUrl: String(item?.linkUrl || item?.link_url || item?.href || "").trim(),
      active: item?.active !== false,
    }))
    .filter((item) => item.name || item.offerText || item.logoUrl);
}

const HERO_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const FAQ_TEMPLATE_ITEMS = [
  {
    id: "requestAtGig",
    question: "Voinko toivoa biisejä keikalla?",
    answer:
      "Kyllä. Kun biisitoiveet ovat auki, voit lähettää toiveen suoraan tältä Paranoidi-sivulta.",
  },
  {
    id: "requestBeforeGig",
    question: "Voinko lähettää biisitoiveen ennen keikkaa?",
    answer:
      "Kyllä, jos biisitoiveet ovat auki. Esiintyjä päättää itse, milloin toiveita otetaan vastaan.",
  },
  {
    id: "viewRepertoire",
    question: "Voinko nähdä ohjelmistosi etukäteen?",
    answer:
      "Kyllä, jos ohjelmisto on asetettu näkyviin. Voit selata kappaleita ja hakea biisejä tältä sivulta.",
  },
  {
    id: "pageWhenRequestsClosed",
    question: "Voinko käyttää sivua, vaikka biisitoiveet eivät ole auki?",
    answer:
      "Kyllä. Sivu toimii edelleen esiintyjän julkisena promosivuna. Voit nähdä esittelyn, ohjelmiston, linkit ja yhteydenottotavat sen mukaan, mitä sivulle on lisätty.",
  },
  {
    id: "allRequestsPlayed",
    question: "Voinko saada kaikki biisitoiveet toteutumaan?",
    answer:
      "Kaikkia toiveita ei voida aina luvata toteuttaa. Esiintyjä valitsee tilanteeseen, aikatauluun ja ohjelmistoon sopivat kappaleet.",
  },
  {
    id: "bookWedding",
    question: "Voinko varata esiintyjän häihin?",
    answer:
      "Kyllä. Tarkemmat tiedot saatavuudesta, kokoonpanosta ja ohjelmistosta kannattaa varmistaa ottamalla yhteyttä esiintyjään.",
  },
  {
    id: "bookCorporate",
    question: "Voinko varata esiintyjän yritysjuhliin tai tapahtumaan?",
    answer:
      "Kyllä. Esiintyjä voi sopia esimerkiksi yritysjuhliin, ravintolailtoihin, yksityistilaisuuksiin ja muihin tapahtumiin.",
  },
  {
    id: "contactPerformer",
    question: "Voinko ottaa yhteyttä esiintyjään tältä sivulta?",
    answer:
      "Kyllä, jos sivulle on lisätty yhteydenottotavat, linkit tai WhatsApp-yhteys.",
  },
];

function normalizeFaqList(value) {
  const items = Array.isArray(value) ? value : [];

  return items
    .slice(0, 8)
    .map((item) => ({
      id: String(item?.id || `faq-${Date.now()}-${Math.random()}`)
        .trim()
        .slice(0, 80),
      question: String(item?.question || item?.title || item?.q || "")
        .trim()
        .slice(0, 160),
      answer: String(item?.answer || item?.text || item?.a || "")
        .trim()
        .slice(0, 900),
      active: item?.active !== false,
    }))
    .filter((item) => item.question || item.answer);
}

function normalizeSettings(settings) {
  return {
    ...(settings || {}),
    publicTitle: settings?.publicTitle || settings?.public_title || "",
    publicIntro: settings?.publicIntro || settings?.public_intro || "",
    publicTitleEn:
      settings?.publicTitleEn || settings?.public_title_en || "",
    publicIntroEn:
      settings?.publicIntroEn || settings?.public_intro_en || "",
        seoTitle: settings?.seoTitle || settings?.seo_title || "",
    seoDescription:
      settings?.seoDescription || settings?.seo_description || "",
    seoTitleEn: settings?.seoTitleEn || settings?.seo_title_en || "",
    seoDescriptionEn:
      settings?.seoDescriptionEn || settings?.seo_description_en || "",
    faqItems: normalizeFaqList(settings?.faqItems || settings?.faq_items || []),
    faqItemsEn: normalizeFaqList(
      settings?.faqItemsEn || settings?.faq_items_en || []
    ),
    heroImageUrl: settings?.heroImageUrl || settings?.hero_image_url || "",
    whatsappNumber: settings?.whatsappNumber || settings?.whatsapp_number || "",
    whatsappMessage:
      settings?.whatsappMessage || settings?.whatsapp_message || "",
    whatsappFloatingEnabled: Boolean(
      settings?.whatsappFloatingEnabled ??
        settings?.whatsapp_floating_enabled ??
        false
    ),
    websiteUrl: settings?.websiteUrl || settings?.website_url || "",
    instagramUrl: settings?.instagramUrl || settings?.instagram_url || "",
    tiktokUrl: settings?.tiktokUrl || settings?.tiktok_url || "",
    youtubeUrl: settings?.youtubeUrl || settings?.youtube_url || "",
    facebookUrl: settings?.facebookUrl || settings?.facebook_url || "",
    spotifyUrl: settings?.spotifyUrl || settings?.spotify_url || "",
    requestSponsors: normalizeSponsorList(
      settings?.requestSponsors ||
        settings?.request_sponsors ||
        []
    ),
    requestRaffleEnabled: Boolean(
      settings?.requestRaffleEnabled ??
        settings?.request_raffle_enabled ??
        false
    ),

    paidRequestBoostsEnabled: Boolean(
      settings?.paidRequestBoostsEnabled ??
        settings?.paid_request_boosts_enabled ??
        false
    ),

    paidRequestBoostAmountCents:
      Number(
        settings?.paidRequestBoostAmountCents ??
          settings?.paid_request_boost_amount_cents
      ) || 300,

    randomEnabled: Boolean(
      settings?.randomEnabled ??
        settings?.random_enabled ??
        true
    ),

    isLiveActive: Boolean(settings?.isLiveActive ?? settings?.is_live_active),
    maxRequestsPerSet:
      settings?.maxRequestsPerSet || settings?.max_requests_per_set || 20,
    publicSongLanguageMode:
      settings?.publicSongLanguageMode ||
      settings?.public_song_language_mode ||
      "all",
  };
}

export default function SettingsPage({ config }) {
  const { t, i18n } = useTranslation("admin");

  function createLocalizedRequestError(data, fallbackMessage) {
    const error = new Error(fallbackMessage);

    error.code = data?.code || "";
    error.payload = data || {};
    error.backendMessage = String(data?.error || "").trim();

    return error;
  }

  function getLocalizedErrorMessage(error, fallbackMessage) {
    const message = String(error?.message || "").trim();

    return error?.backendMessage !== undefined && message
      ? message
      : fallbackMessage;
  }

  const adminLocale =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en")
      ? "en-GB"
      : "fi-FI";

  const documentLanguage =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en")
      ? "en"
      : "fi";

  const heroImageTooLargeMessage = t("settingsPage.messages.heroImageTooLarge", {
    defaultValue:
      "Kuva on liian suuri. Suurin sallittu koko on 5 Mt. Pienennä kuva ja yritä uudelleen.",
  });

  const initialSettings = normalizeSettings(getParanoidiSettings());
  const [settings, setSettings] = useState(initialSettings);
  const [contentLanguage, setContentLanguage] = useState(() =>
    String(i18n.resolvedLanguage || i18n.language || "fi")
      .toLowerCase()
      .startsWith("en")
      ? "en"
      : "fi"
  );
  const [publicTitleDraft, setPublicTitleDraft] = useState(
    initialSettings.publicTitle || ""
  );
  const [publicIntroDraft, setPublicIntroDraft] = useState(
    initialSettings.publicIntro || ""
  );
  const [publicTitleEnDraft, setPublicTitleEnDraft] = useState(
    initialSettings.publicTitleEn || ""
  );
  const [publicIntroEnDraft, setPublicIntroEnDraft] = useState(
    initialSettings.publicIntroEn || ""
  );
  const [seoTitleDraft, setSeoTitleDraft] = useState(
    initialSettings.seoTitle || ""
  );
  const [seoDescriptionDraft, setSeoDescriptionDraft] = useState(
    initialSettings.seoDescription || ""
  );
  const [seoTitleEnDraft, setSeoTitleEnDraft] = useState(
    initialSettings.seoTitleEn || ""
  );
  const [seoDescriptionEnDraft, setSeoDescriptionEnDraft] = useState(
    initialSettings.seoDescriptionEn || ""
  );
  const [faqDrafts, setFaqDrafts] = useState(() =>
    normalizeFaqList(initialSettings.faqItems || [])
  );
  const [faqDraftsEn, setFaqDraftsEn] = useState(() =>
    normalizeFaqList(initialSettings.faqItemsEn || [])
  );
  const [heroImageUrlDraft, setHeroImageUrlDraft] = useState(
    getHeroImageUrl(initialSettings)
  );
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImageVersion, setHeroImageVersion] = useState(() => Date.now());
  const [heroImageInputKey, setHeroImageInputKey] = useState(0);
  const [selectedHeroPreviewUrl, setSelectedHeroPreviewUrl] = useState("");
  const [whatsappNumberDraft, setWhatsappNumberDraft] = useState(initialSettings.whatsappNumber || "");
  const [whatsappMessageDraft, setWhatsappMessageDraft] = useState(initialSettings.whatsappMessage || "");
  const [whatsappFloatingEnabledDraft, setWhatsappFloatingEnabledDraft] =
    useState(Boolean(initialSettings.whatsappFloatingEnabled));
  const [websiteUrlDraft, setWebsiteUrlDraft] = useState(initialSettings.websiteUrl || "");
  const [instagramUrlDraft, setInstagramUrlDraft] = useState(initialSettings.instagramUrl || "");
  const [tiktokUrlDraft, setTiktokUrlDraft] = useState(initialSettings.tiktokUrl || "");
  const [youtubeUrlDraft, setYoutubeUrlDraft] = useState(initialSettings.youtubeUrl || "");
  const [facebookUrlDraft, setFacebookUrlDraft] = useState(initialSettings.facebookUrl || "");
  const [spotifyUrlDraft, setSpotifyUrlDraft] = useState(initialSettings.spotifyUrl || "");
  const [sponsorsDraft, setSponsorsDraft] = useState(() =>
    normalizeSponsorList(initialSettings.requestSponsors || [])
  );
  const [sponsorLogoMessages, setSponsorLogoMessages] = useState({});
  const [uploadingSponsorIndex, setUploadingSponsorIndex] = useState(null);
    const [isLiveActiveDraft, setIsLiveActiveDraft] = useState(
    Boolean(initialSettings.isLiveActive)
  );
  const [requestRaffleEnabledDraft, setRequestRaffleEnabledDraft] =
    useState(Boolean(initialSettings.requestRaffleEnabled));
  const [
    paidRequestBoostsEnabledDraft,
    setPaidRequestBoostsEnabledDraft,
  ] = useState(Boolean(initialSettings.paidRequestBoostsEnabled));

  const [stripeConnectStatus, setStripeConnectStatus] = useState({
    isLoading: true,
    connected: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false,
    requirementsCurrentlyDue: [],
    error: "",
  });

  const [isStartingStripeConnect, setIsStartingStripeConnect] =
    useState(false);

  const [stripeConnectMessage, setStripeConnectMessage] =
    useState("");

  const [showStripeHelp, setShowStripeHelp] = useState(false);

  const [randomEnabledDraft, setRandomEnabledDraft] = useState(
    Boolean(initialSettings.randomEnabled)
  );
  const [maxRequestsDraft, setMaxRequestsDraft] = useState(
    Number(initialSettings.maxRequestsPerSet) || 20
  );
  const [languageModeDraft, setLanguageModeDraft] = useState(
    initialSettings.publicSongLanguageMode || "all"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);
  const [message, setMessage] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [showPublicQr, setShowPublicQr] = useState(false);

  const isEditingEnglishContent = contentLanguage === "en";

  const activePublicTitleDraft = isEditingEnglishContent
    ? publicTitleEnDraft
    : publicTitleDraft;
  const activePublicIntroDraft = isEditingEnglishContent
    ? publicIntroEnDraft
    : publicIntroDraft;
  const activeSeoTitleDraft = isEditingEnglishContent
    ? seoTitleEnDraft
    : seoTitleDraft;
  const activeSeoDescriptionDraft = isEditingEnglishContent
    ? seoDescriptionEnDraft
    : seoDescriptionDraft;
  const activeFaqDrafts = isEditingEnglishContent ? faqDraftsEn : faqDrafts;

  const faqTemplateItems = FAQ_TEMPLATE_ITEMS.map((item) => ({
    ...item,
    question: t(`settingsPage.faqTemplates.${item.id}.question`, {
      lng: contentLanguage,
      defaultValue: item.question,
    }),
    answer: t(`settingsPage.faqTemplates.${item.id}.answer`, {
      lng: contentLanguage,
      defaultValue: item.answer,
    }),
  }));

  function setActivePublicTitleDraft(value) {
    if (isEditingEnglishContent) {
      setPublicTitleEnDraft(value);
      return;
    }

    setPublicTitleDraft(value);
  }

  function setActivePublicIntroDraft(value) {
    if (isEditingEnglishContent) {
      setPublicIntroEnDraft(value);
      return;
    }

    setPublicIntroDraft(value);
  }

  function setActiveSeoTitleDraft(value) {
    if (isEditingEnglishContent) {
      setSeoTitleEnDraft(value);
      return;
    }

    setSeoTitleDraft(value);
  }

  function setActiveSeoDescriptionDraft(value) {
    if (isEditingEnglishContent) {
      setSeoDescriptionEnDraft(value);
      return;
    }

    setSeoDescriptionDraft(value);
  }

  const performerSlug = getPerformerSlug();
  const publicChannelPath = `/p/${performerSlug}`;
  const publicChannelUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${publicChannelPath}`
      : publicChannelPath;

  const currentPerformer = getCurrentPerformer();
  const currentFeatures = currentPerformer?.features || {};

  const performerDisplayName =
    String(
      currentPerformer?.name ||
        currentPerformer?.performerName ||
        currentPerformer?.performer_name ||
        t("settingsPage.common.performerFallback", {
          defaultValue: "Esiintyjä",
        })
    ).trim() ||
    t("settingsPage.common.performerFallback", {
      defaultValue: "Esiintyjä",
    });

  const canUseSocialLinks = currentFeatures.socialLinksEnabled === true;
  const canUseWhatsapp = currentFeatures.whatsappEnabled === true;
  const canUsePublicBranding = currentFeatures.publicBrandingEnabled === true;
  const canUseSeoFaq = currentFeatures.seoFaqEnabled === true;
  const canUseRequestSponsors = currentFeatures.setlistsEnabled === true;

  const stripeConnectReady =
    stripeConnectStatus.connected &&
    stripeConnectStatus.chargesEnabled &&
    stripeConnectStatus.payoutsEnabled &&
    stripeConnectStatus.detailsSubmitted;

  const paidRequestBoostAmountCents =
    Number(
      settings?.paidRequestBoostAmountCents ??
        settings?.paid_request_boost_amount_cents ??
        initialSettings.paidRequestBoostAmountCents ??
        300
    ) || 300;

  const paidRequestBoostAmountLabel =
    `${(paidRequestBoostAmountCents / 100).toLocaleString(adminLocale, {
      maximumFractionDigits: 2,
    })} €`;

  /*
   * Jos Stripe-statuksen tarkistus on vielä kesken tai epäonnistuu,
   * muiden asetusten tallennus ei saa vahingossa sammuttaa jo käytössä
   * olevaa näkyvyysnostoa.
   */
  const paidRequestBoostsEnabledForSave =
    stripeConnectStatus.isLoading || Boolean(stripeConnectStatus.error)
      ? Boolean(
          settings?.paidRequestBoostsEnabled ??
            settings?.paid_request_boosts_enabled ??
            false
        )
      : stripeConnectReady && paidRequestBoostsEnabledDraft;

  const heroImagePreviewUrl = addCacheBuster(
    heroImageUrlDraft,
    heroImageVersion
  );

  const heroPreviewSrc = selectedHeroPreviewUrl || heroImagePreviewUrl;

  useEffect(() => {
    if (!heroImageFile) {
      setSelectedHeroPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(heroImageFile);
    setSelectedHeroPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [heroImageFile]);

  useEffect(() => {
    if (!showPublicQr) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [showPublicQr]);

  useEffect(() => {
  if (!isLiveActiveDraft && requestRaffleEnabledDraft) {
    setRequestRaffleEnabledDraft(false);
  }
}, [isLiveActiveDraft, requestRaffleEnabledDraft]);

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      setIsLoading(false);
      setMessage("");
      setUploadMessage("");

      const loadedSettings = normalizeSettings(await fetchParanoidiSettings());

      if (!isMounted) {
        return;
      }

      setSettings(loadedSettings);
      setPublicTitleDraft(loadedSettings.publicTitle || "");
      setPublicIntroDraft(loadedSettings.publicIntro || "");
      setPublicTitleEnDraft(loadedSettings.publicTitleEn || "");
      setPublicIntroEnDraft(loadedSettings.publicIntroEn || "");
      setSeoTitleDraft(loadedSettings.seoTitle || "");
      setSeoDescriptionDraft(loadedSettings.seoDescription || "");
      setSeoTitleEnDraft(loadedSettings.seoTitleEn || "");
      setSeoDescriptionEnDraft(loadedSettings.seoDescriptionEn || "");
      setFaqDrafts(normalizeFaqList(loadedSettings.faqItems || []));
      setFaqDraftsEn(normalizeFaqList(loadedSettings.faqItemsEn || []));
      setHeroImageUrlDraft(getHeroImageUrl(loadedSettings));
      setHeroImageVersion(Date.now());
      setWhatsappNumberDraft(loadedSettings.whatsappNumber || "");
      setWhatsappMessageDraft(loadedSettings.whatsappMessage || "");
      setWhatsappFloatingEnabledDraft(
        Boolean(loadedSettings.whatsappFloatingEnabled)
      );
      setWebsiteUrlDraft(loadedSettings.websiteUrl || "");
      setInstagramUrlDraft(loadedSettings.instagramUrl || "");
      setTiktokUrlDraft(loadedSettings.tiktokUrl || "");
      setYoutubeUrlDraft(loadedSettings.youtubeUrl || "");
      setFacebookUrlDraft(loadedSettings.facebookUrl || "");
      setSpotifyUrlDraft(loadedSettings.spotifyUrl || "");
      setSponsorsDraft(normalizeSponsorList(loadedSettings.requestSponsors || []));
      setIsLiveActiveDraft(Boolean(loadedSettings.isLiveActive));
      setRequestRaffleEnabledDraft(
        Boolean(loadedSettings.requestRaffleEnabled)
      );
      setPaidRequestBoostsEnabledDraft(
        Boolean(loadedSettings.paidRequestBoostsEnabled)
      );
      setRandomEnabledDraft(Boolean(loadedSettings.randomEnabled));
      setMaxRequestsDraft(Number(loadedSettings.maxRequestsPerSet) || 20);
      setLanguageModeDraft(loadedSettings.publicSongLanguageMode || "all");
      setIsLoading(false);
    }

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadStripeConnectStatus() {
      setStripeConnectStatus((current) => ({
        ...current,
        isLoading: true,
        error: "",
      }));

      try {
        const response = await fetchWithCredentials(
          `${getApiBaseUrl()}/admin/performers/${encodeURIComponent(
            performerSlug
          )}/payments/connect/status`
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data?.ok) {
          throw createLocalizedRequestError(
            data,
            t("settingsPage.messages.stripeStatusFailed", {
              defaultValue:
                "Stripe-yhteyden tilan tarkistus epäonnistui.",
            })
          );
        }

        if (!isMounted) {
          return;
        }

        setStripeConnectStatus({
          isLoading: false,
          connected: data.connected === true,
          chargesEnabled:
            data.chargesEnabled === true ||
            data.charges_enabled === true,
          payoutsEnabled:
            data.payoutsEnabled === true ||
            data.payouts_enabled === true,
          detailsSubmitted:
            data.detailsSubmitted === true ||
            data.details_submitted === true,
          requirementsCurrentlyDue: Array.isArray(
            data.requirementsCurrentlyDue
          )
            ? data.requirementsCurrentlyDue
            : Array.isArray(data.requirements_currently_due)
              ? data.requirements_currently_due
              : [],
          error: "",
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setStripeConnectStatus({
          isLoading: false,
          connected: false,
          chargesEnabled: false,
          payoutsEnabled: false,
          detailsSubmitted: false,
          requirementsCurrentlyDue: [],
          error: getLocalizedErrorMessage(
            error,
            t("settingsPage.messages.stripeStatusFailed", {
              defaultValue:
                "Stripe-yhteyden tilan tarkistus epäonnistui.",
            })
          ),
        });
      }
    }

    loadStripeConnectStatus();

    return () => {
      isMounted = false;
    };
  }, [performerSlug, t]);

function buildSettingsPayload(nextHeroImageUrl = heroImageUrlDraft) {
  return {
    publicTitle: publicTitleDraft,
    publicIntro: publicIntroDraft,
    publicTitleEn: publicTitleEnDraft,
    publicIntroEn: publicIntroEnDraft,
    seoTitle: seoTitleDraft,
    seoDescription: seoDescriptionDraft,
    seoTitleEn: seoTitleEnDraft,
    seoDescriptionEn: seoDescriptionEnDraft,
    faqItems: normalizeFaqList(faqDrafts),
    faqItemsEn: normalizeFaqList(faqDraftsEn),
    heroImageUrl: nextHeroImageUrl,
    whatsappNumber: whatsappNumberDraft,
    whatsappMessage: whatsappMessageDraft,
    whatsappFloatingEnabled: whatsappFloatingEnabledDraft,
    websiteUrl: websiteUrlDraft,
    instagramUrl: instagramUrlDraft,
    tiktokUrl: tiktokUrlDraft,
    youtubeUrl: youtubeUrlDraft,
    facebookUrl: facebookUrlDraft,
    spotifyUrl: spotifyUrlDraft,
    requestSponsors: canUseRequestSponsors
      ? normalizeSponsorList(sponsorsDraft)
      : normalizeSponsorList(settings?.requestSponsors || settings?.request_sponsors || []),
    requestRaffleEnabled: isLiveActiveDraft && requestRaffleEnabledDraft,
    request_raffle_enabled: isLiveActiveDraft && requestRaffleEnabledDraft,
    paidRequestBoostsEnabled:
      paidRequestBoostsEnabledForSave,
    paid_request_boosts_enabled:
      paidRequestBoostsEnabledForSave,

    randomEnabled: randomEnabledDraft,
    random_enabled: randomEnabledDraft,
    isLiveActive: isLiveActiveDraft,
    maxRequestsPerSet: Number(maxRequestsDraft) || 20,
    publicSongLanguageMode: languageModeDraft,
  };
}

async function handleStripeConnect() {
  setIsStartingStripeConnect(true);
  setStripeConnectMessage("");

  try {
    const response = await fetchWithCredentials(
      `${getApiBaseUrl()}/admin/performers/${encodeURIComponent(
        performerSlug
      )}/payments/connect/onboarding`,
      {
        method: "POST",
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data?.ok || !data?.url) {
      throw createLocalizedRequestError(
        data,
        t("settingsPage.messages.stripeOnboardingFailed", {
          defaultValue:
            "Stripe-käyttöönoton aloittaminen epäonnistui.",
        })
      );
    }

    window.location.assign(data.url);
  } catch (error) {
    setStripeConnectMessage(
      getLocalizedErrorMessage(
        error,
        t("settingsPage.messages.stripeOnboardingFailed", {
          defaultValue:
            "Stripe-käyttöönoton aloittaminen epäonnistui.",
        })
      )
    );

    setIsStartingStripeConnect(false);
  }
}

async function handleSaveSettings(event) {
  event.preventDefault();

  setIsSaving(true);
  setMessage("");

  try {
    const savedSettings = await saveParanoidiSettings(buildSettingsPayload());

    const nextHeroImageUrl = getHeroImageUrl(savedSettings);

    setSettings(savedSettings);
    setPaidRequestBoostsEnabledDraft(
      Boolean(
        savedSettings.paidRequestBoostsEnabled ??
          savedSettings.paid_request_boosts_enabled
      )
    );
    setHeroImageUrlDraft(nextHeroImageUrl);
    setHeroImageVersion(Date.now());
    setMessage(
      t("settingsPage.messages.settingsSaved", {
        defaultValue: "Asetukset tallennettu.",
      })
    );
  } catch (error) {
    setMessage(
      error.message ||
        t("settingsPage.messages.settingsSaveFailed", {
          defaultValue: "Asetusten tallennus epäonnistui.",
        })
    );
  } finally {
    setIsSaving(false);
  }
}

async function handleUploadHeroImage() {
  if (!heroImageFile) {
    setUploadMessage(
      t("settingsPage.messages.selectImageFirst", {
        defaultValue: "Valitse ensin kuva.",
      })
    );
    return;
  }

  if (heroImageFile.size > HERO_IMAGE_MAX_BYTES) {
    setUploadMessage(heroImageTooLargeMessage);
    return;
  }

  setIsUploadingHeroImage(true);
  setUploadMessage("");

  try {
    const formData = new FormData();
    formData.append("image", heroImageFile);

    const performerSlug = getPerformerSlug();

    const response = await fetchWithCredentials(
      `${getApiBaseUrl()}/admin/performers/${encodeURIComponent(
        performerSlug
      )}/hero-image`,
      {
        method: "POST",
        body: formData,
      }
    );

    const responseText = await response.text();

    let data = null;

    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch {
      data = null;
    }

    if (!response.ok || !data?.ok) {
      if (response.status === 413) {
        throw createLocalizedRequestError(null, heroImageTooLargeMessage);
      }

      throw createLocalizedRequestError(
        data,
        t("settingsPage.messages.heroUploadFailedCheck", {
          defaultValue:
            "Kuvan lataus epäonnistui. Tarkista kuvan koko ja yritä uudelleen.",
        })
      );
    }

    const nextSettings = {
      ...(settings || {}),
      ...(data.settings || {}),
    };

    const nextHeroImageUrl =
      getHeroImageUrl(data.settings) || getHeroImageUrl(nextSettings);

    const normalizedSettings = {
      ...nextSettings,
      heroImageUrl: nextHeroImageUrl,
      hero_image_url: nextHeroImageUrl,
    };

    setSettings(normalizedSettings);
    setHeroImageUrlDraft(nextHeroImageUrl);
    setHeroImageFile(null);
    setHeroImageVersion(Date.now());
    setHeroImageInputKey((current) => current + 1);
    setUploadMessage(
      t("settingsPage.messages.heroUploaded", {
        defaultValue:
          "Kuva ladattu ja otettu käyttöön. Päivitä yleisösivu, jos vanha kuva näkyy vielä välimuistista.",
      })
    );
  } catch (error) {
    setUploadMessage(
      getLocalizedErrorMessage(
        error,
        t("settingsPage.messages.heroUploadFailed", {
          defaultValue: "Kuvan lataus epäonnistui.",
        })
      )
    );
  } finally {
    setIsUploadingHeroImage(false);
  }
}

async function handleRemoveHeroImage() {
  if (!heroImageUrlDraft) {
    setUploadMessage(
      t("settingsPage.messages.noImageToRemove", {
        defaultValue: "Poistettavaa kuvaa ei ole.",
      })
    );
    return;
  }

  setIsUploadingHeroImage(true);
  setUploadMessage("");

  try {
    const savedSettings = await saveParanoidiSettings(buildSettingsPayload(""));

    const normalizedSettings = {
      ...savedSettings,
      heroImageUrl: "",
      hero_image_url: "",
    };

    setSettings(normalizedSettings);
    setHeroImageUrlDraft("");
    setHeroImageFile(null);
    setHeroImageVersion(Date.now());
    setHeroImageInputKey((current) => current + 1);
    setUploadMessage(
      t("settingsPage.messages.heroRemoved", {
        defaultValue: "Kuva poistettu julkiselta sivulta.",
      })
    );
  } catch (error) {
    setUploadMessage(
      error.message ||
        t("settingsPage.messages.heroRemoveFailed", {
          defaultValue: "Kuvan poisto epäonnistui.",
        })
    );
  } finally {
    setIsUploadingHeroImage(false);
  }
}

  function addSponsorDraft() {
    if (!canUseRequestSponsors) {
      setMessage(
        t("settingsPage.messages.sponsorsPlusOnly", {
          defaultValue: "Sponsorit kuuluvat Plus-pakettiin.",
        })
      );
      return;
    }

    setSponsorsDraft((current) => {
    if (current.length >= 5) {
      setMessage(
        t("settingsPage.messages.maxSponsors", {
          defaultValue: "Voit lisätä enintään 5 sponsoria.",
        })
      );
      return current;
    }

    return [
      ...current,
      {
        id: `sponsor-${Date.now()}`,
        label: t("settingsPage.sponsors.defaultLabel", {
          defaultValue: "Tämän toiveen tarjoaa",
        }),
        name: "",
        offerText: "",
        logoUrl: "",
        linkUrl: "",
        active: true,
      },
    ];
  });
}

function updateSponsorDraft(index, updates) {
  setSponsorsDraft((current) =>
    current.map((sponsor, currentIndex) =>
      currentIndex === index
        ? {
            ...sponsor,
            ...updates,
          }
        : sponsor
    )
  );
}

function removeSponsorDraft(index) {
  setSponsorsDraft((current) =>
    current.filter((_, currentIndex) => currentIndex !== index)
  );
}

async function handleUploadSponsorLogo(index, file) {
  if (!file) {
    return;
  }

  const allowedTypes = new Set(["image/png", "image/jpeg", "image/webp"]);

  if (!allowedTypes.has(file.type)) {
    setSponsorLogoMessages((current) => ({
      ...current,
      [index]: t("settingsPage.messages.sponsorLogoType", {
        defaultValue: "Vain PNG-, JPG- ja WebP-logot sallitaan.",
      }),
    }));
    return;
  }

  if (file.size > 1024 * 1024) {
    setSponsorLogoMessages((current) => ({
      ...current,
      [index]: t("settingsPage.messages.sponsorLogoTooLarge", {
        defaultValue: "Logo on liian suuri. Suurin sallittu koko on 1 Mt.",
      }),
    }));
    return;
  }

  setUploadingSponsorIndex(index);
  setSponsorLogoMessages((current) => ({
    ...current,
    [index]: "",
  }));

  try {
    const formData = new FormData();
    formData.append("logo", file);

    const response = await fetchWithCredentials(
      `${getApiBaseUrl()}/admin/performers/${encodeURIComponent(
        getPerformerSlug()
      )}/sponsor-logo`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw createLocalizedRequestError(
        data,
        t("settingsPage.messages.sponsorLogoUploadFailed", {
          defaultValue: "Sponsorilogon lataus epäonnistui.",
        })
      );
    }

    updateSponsorDraft(index, {
      logoUrl: data.logoUrl || data.logo_url || "",
    });

    setSponsorLogoMessages((current) => ({
      ...current,
      [index]: t("settingsPage.messages.sponsorLogoUploaded", {
        defaultValue: "Logo ladattu. Muista tallentaa asetukset.",
      }),
    }));
  } catch (error) {
    setSponsorLogoMessages((current) => ({
      ...current,
      [index]: getLocalizedErrorMessage(
        error,
        t("settingsPage.messages.sponsorLogoUploadFailed", {
          defaultValue: "Sponsorilogon lataus epäonnistui.",
        })
      ),
    }));
  } finally {
    setUploadingSponsorIndex(null);
  }
}

function addFaqDraft(item = {}, languageCode = contentLanguage) {
  const setDrafts = languageCode === "en" ? setFaqDraftsEn : setFaqDrafts;

  setDrafts((current) => {
    if (current.length >= 8) {
      setMessage(
        t("settingsPage.messages.maxFaq", {
          defaultValue: "Voit lisätä enintään 8 usein kysyttyä kysymystä.",
        })
      );
      return current;
    }

    return [
      ...current,
      {
        id: `faq-${Date.now()}`,
        question: String(item.question || "").slice(0, 160),
        answer: String(item.answer || "").slice(0, 900),
        active: item.active !== false,
      },
    ];
  });
}

function updateFaqDraft(index, updates, languageCode = contentLanguage) {
  const setDrafts = languageCode === "en" ? setFaqDraftsEn : setFaqDrafts;

  setDrafts((current) =>
    current.map((item, currentIndex) =>
      currentIndex === index
        ? {
            ...item,
            ...updates,
          }
        : item
    )
  );
}

function removeFaqDraft(index, languageCode = contentLanguage) {
  const setDrafts = languageCode === "en" ? setFaqDraftsEn : setFaqDrafts;

  setDrafts((current) =>
    current.filter((_, currentIndex) => currentIndex !== index)
  );
}

function renderContentLanguageSwitcher(helpKey, defaultHelp) {
  return (
    <div className="grid gap-3 rounded-2xl border border-[#5a3824]/12 bg-[#fff8ef]/65 p-3 theme-dark:border-white/10 theme-dark:bg-zinc-950 sm:p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
            {t("settingsPage.contentLanguage.label", {
              defaultValue: "Muokattava sisältökieli",
            })}
          </p>

          <p className="mt-1 max-w-3xl text-xs font-semibold leading-5 text-[#6b4229] theme-dark:text-zinc-400">
            {t(helpKey, {
              defaultValue: defaultHelp,
            })}
          </p>
        </div>

        <div
          className="inline-grid shrink-0 grid-cols-2 gap-1 rounded-full border border-[#5a3824]/15 bg-white/70 p-1 theme-dark:border-white/10 theme-dark:bg-black/40"
          role="group"
          aria-label={t("settingsPage.contentLanguage.label", {
            defaultValue: "Muokattava sisältökieli",
          })}
        >
          <button
            type="button"
            aria-pressed={contentLanguage === "fi"}
            onClick={() => setContentLanguage("fi")}
            className={[
              "rounded-full px-4 py-2 text-xs font-black transition",
              contentLanguage === "fi"
                ? "bg-red-500 text-white shadow-sm"
                : "text-[#6b4229] hover:bg-white theme-dark:text-zinc-400 theme-dark:hover:bg-white/10",
            ].join(" ")}
          >
            🇫🇮{" "}
            {t("settingsPage.contentLanguage.fi", {
              defaultValue: "Suomi",
            })}
          </button>

          <button
            type="button"
            aria-pressed={contentLanguage === "en"}
            onClick={() => setContentLanguage("en")}
            className={[
              "rounded-full px-4 py-2 text-xs font-black transition",
              contentLanguage === "en"
                ? "bg-red-500 text-white shadow-sm"
                : "text-[#6b4229] hover:bg-white theme-dark:text-zinc-400 theme-dark:hover:bg-white/10",
            ].join(" ")}
          >
            🇬🇧{" "}
            {t("settingsPage.contentLanguage.en", {
              defaultValue: "English",
            })}
          </button>
        </div>
      </div>
    </div>
  );
}

  async function handleCopyPublicChannelUrl() {
    try {
      await navigator.clipboard.writeText(publicChannelUrl);
      setMessage(
        t("settingsPage.messages.publicLinkCopied", {
          defaultValue: "Yleisölinkki kopioitu.",
        })
      );
    } catch {
      setMessage(
        t("settingsPage.messages.copyFailed", {
          defaultValue:
            "Kopiointi ei onnistunut. Voit kopioida linkin käsin.",
        })
      );
    }
  }


  function getPublicQrSvgMarkup() {
    const svg = document.getElementById("paranoidi-public-qr-svg");

    if (!svg) {
      throw new Error(
        t("settingsPage.messages.qrNotFound", {
          defaultValue:
            "QR-koodia ei löytynyt. Päivitä sivu ja yritä uudelleen.",
        })
      );
    }

    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");

    return new XMLSerializer().serializeToString(clone);
  }

  function downloadQrFile(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  function handleDownloadQrSvg() {
    try {
      const svgMarkup = getPublicQrSvgMarkup();

      downloadQrFile(
        new Blob([svgMarkup], {
          type: "image/svg+xml;charset=utf-8",
        }),
        `paranoidi-${performerSlug}-qr.svg`
      );
    } catch (error) {
      setMessage(
        error.message ||
          t("settingsPage.messages.qrDownloadFailed", {
            defaultValue: "QR-koodin lataus epäonnistui.",
          })
      );
    }
  }

  function handleDownloadQrPng() {
    try {
      const svgMarkup = getPublicQrSvgMarkup();

      const svgBlob = new Blob([svgMarkup], {
        type: "image/svg+xml;charset=utf-8",
      });

      const svgUrl = URL.createObjectURL(svgBlob);
      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 1600;

        canvas.width = size;
        canvas.height = size;

        const context = canvas.getContext("2d");

        if (!context) {
          URL.revokeObjectURL(svgUrl);
          setMessage(
            t("settingsPage.messages.qrPngFailed", {
              defaultValue: "QR-koodin PNG-kuvan luonti epäonnistui.",
            })
          );
          return;
        }

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, size, size);
        context.drawImage(image, 0, 0, size, size);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(svgUrl);

            if (!blob) {
              setMessage(
            t("settingsPage.messages.qrPngFailed", {
              defaultValue: "QR-koodin PNG-kuvan luonti epäonnistui.",
            })
          );
              return;
            }

            downloadQrFile(
              blob,
              `paranoidi-${performerSlug}-qr.png`
            );
          },
          "image/png",
          1
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        setMessage(
            t("settingsPage.messages.qrPngFailed", {
              defaultValue: "QR-koodin PNG-kuvan luonti epäonnistui.",
            })
          );
      };

      image.src = svgUrl;
    } catch (error) {
      setMessage(
        error.message ||
          t("settingsPage.messages.qrDownloadFailed", {
            defaultValue: "QR-koodin lataus epäonnistui.",
          })
      );
    }
  }

  function handlePrintQrPoster() {
    try {
      const svgMarkup = getPublicQrSvgMarkup();

      const escapeHtml = (value) =>
        String(value || "").replace(
          /[&<>"']/g,
          (character) =>
            ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              '"': "&quot;",
              "'": "&#039;",
            })[character]
        );

      const safePerformerName = escapeHtml(performerDisplayName);
      const safePublicUrl = escapeHtml(publicChannelUrl);
      const safePosterHeading = escapeHtml(
        t("settingsPage.qr.posterHeading", {
          defaultValue: "Toivo biisiä",
        })
      );
      const safePosterInstruction = escapeHtml(
        t("settingsPage.qr.posterInstruction", {
          defaultValue:
            "Skannaa QR-koodi ja lähetä biisitoive suoraan esiintyjälle.",
        })
      );

      const printWindow = window.open(
        "",
        "_blank",
        "width=900,height=1200"
      );

      if (!printWindow) {
        setMessage(
          t("settingsPage.messages.printPopupFailed", {
            defaultValue:
              "Tulostusikkunaa ei voitu avata. Salli ponnahdusikkunat ja yritä uudelleen.",
          })
        );
        return;
      }

      printWindow.document.write(`
        <!doctype html>
        <html lang="${documentLanguage}">
          <head>
            <meta charset="utf-8" />
            <title>Paranoidi QR – ${safePerformerName}</title>

            <style>
              @page {
                size: A4 portrait;
                margin: 0;
              }

              * {
                box-sizing: border-box;
              }

              html,
              body {
                margin: 0;
                padding: 0;
                background: #ffffff;
                color: #18181b;
                font-family: Arial, Helvetica, sans-serif;
              }

              .poster {
                width: 210mm;
                min-height: 297mm;
                padding: 18mm 16mm;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
              }

              .brand {
                margin-bottom: 8mm;
                color: #dc2626;
                font-size: 5mm;
                font-weight: 900;
                letter-spacing: 0.22em;
                text-transform: uppercase;
              }

              h1 {
                margin: 0;
                font-size: 20mm;
                line-height: 0.9;
                font-weight: 900;
                letter-spacing: -0.055em;
                text-transform: uppercase;
              }

              .performer {
                margin-top: 7mm;
                font-size: 8mm;
                line-height: 1.1;
                font-weight: 900;
              }

              .instruction {
                margin: 7mm 0 8mm;
                max-width: 155mm;
                font-size: 5mm;
                line-height: 1.45;
                font-weight: 700;
              }

              .qr {
                width: 118mm;
                height: 118mm;
              }

              .qr svg {
                width: 118mm;
                height: 118mm;
              }

              .url {
                margin-top: 8mm;
                max-width: 170mm;
                overflow-wrap: anywhere;
                font-size: 4mm;
                line-height: 1.4;
                font-weight: 700;
              }

              .footer {
                margin-top: 9mm;
                color: #dc2626;
                font-size: 4mm;
                font-weight: 900;
                letter-spacing: 0.12em;
                text-transform: uppercase;
              }
            </style>
          </head>

          <body>
            <main class="poster">
              <div class="brand">Paranoidi</div>

              <h1>${safePosterHeading}</h1>

              <div class="performer">
                ${safePerformerName}
              </div>

              <p class="instruction">
                ${safePosterInstruction}
              </p>

              <div class="qr">
                ${svgMarkup}
              </div>

              <div class="url">
                ${safePublicUrl}
              </div>

              <div class="footer">
                Paranoidi.fi
              </div>
            </main>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      window.setTimeout(() => {
        printWindow.print();
      }, 300);
    } catch (error) {
      setMessage(
        error.message ||
          t("settingsPage.messages.qrPrintFailed", {
            defaultValue: "QR-kyltin tulostus epäonnistui.",
          })
      );
    }
  }

  const publicQrOverlay =
    showPublicQr && typeof document !== "undefined"
      ? createPortal(
          <div className="fixed inset-0 z-[2147483647] overflow-hidden bg-[#ead6c2] text-[#241a16] transition-colors duration-300 theme-dark:bg-black theme-dark:text-white">
            <div className="flex h-[100svh] w-full flex-col items-center justify-center px-4 py-4 text-center">
              <button
                type="button"
                onClick={() => setShowPublicQr(false)}
                className="absolute right-4 top-4 rounded-full border border-[#5a3824]/18 bg-[#fff8ef]/80 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#541717] shadow-lg transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.06] theme-dark:text-white theme-dark:hover:bg-white/10"
              >
                {t("settingsPage.common.close", {
                  defaultValue: "Sulje",
                })}
              </button>

              <p className="text-xs font-black uppercase tracking-[0.24em] text-[#b42318] theme-dark:text-red-400">
                Paranoidi
              </p>

              <h2 className="mt-3 max-w-xl text-4xl font-black uppercase leading-[0.9] tracking-[-0.06em] text-[#241a16] theme-dark:text-white sm:text-6xl">
                {t("settingsPage.qr.posterHeading", {
                  defaultValue: "Toivo biisiä",
                })}
              </h2>

              <p className="mt-3 max-w-md text-sm font-semibold leading-6 text-[#5a3824] theme-dark:text-zinc-300 sm:text-base">
                {t("settingsPage.qr.posterInstruction", {
                  defaultValue:
                    "Skannaa QR-koodi ja lähetä biisitoive suoraan esiintyjälle.",
                })}
              </p>

              <div className="mt-5 w-[min(72vw,340px)] rounded-[2rem] border border-[#5a3824]/15 bg-white p-4 shadow-[0_24px_70px_rgba(84,23,23,0.20),0_0_55px_rgba(239,68,68,0.12)] theme-dark:border-white/10 theme-dark:shadow-[0_0_80px_rgba(239,68,68,0.25)]">
                <QRCodeSVG
                  value={publicChannelUrl}
                  size={340}
                  level="H"
                  includeMargin
                  className="h-full w-full"
                />
              </div>

              <p className="mt-4 max-w-[min(92vw,700px)] break-all rounded-2xl border border-[#5a3824]/15 bg-[#fff8ef]/85 px-4 py-3 text-xs font-bold leading-5 text-[#541717] shadow-sm theme-dark:border-white/10 theme-dark:bg-zinc-950 theme-dark:text-zinc-300 sm:text-sm">
                {publicChannelUrl}
              </p>

              <p className="mt-3 text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500 sm:text-sm">
                {t("settingsPage.qr.overlayHint", {
                  defaultValue:
                    "Näytä tämä yleisölle puhelimelta, tabletilta tai screeniltä.",
                })}
              </p>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>

      <Navbar config={config} />
      <HelpCenter />

      <main className="settings-page min-h-screen w-full max-w-full overflow-x-clip bg-[#ead6c2] px-3 pb-6 pt-28 text-[#241a16] transition-colors duration-300 theme-dark:bg-zinc-950 theme-dark:text-white sm:px-4 sm:pb-8 sm:pt-32">
        <section className="mx-auto w-full max-w-5xl min-w-0">

          <form
            onSubmit={handleSaveSettings}
            className="settings-page-panel w-full max-w-full min-w-0 overflow-visible rounded-3xl border border-[#5a3824]/10 bg-[#fff8ef]/70 p-4 shadow-2xl shadow-[#3a2b18]/10 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:shadow-black/30 sm:p-6"
          >
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#b42318] theme-dark:text-red-400">
              Paranoidi
            </p>

            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#241a16] theme-dark:text-white">
              {t("settingsPage.title", {
                defaultValue: "Asetukset",
              })}
            </h1>

            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-300">
              {t("settingsPage.description", {
                defaultValue:
                  "Määritä, miltä esiintyjän yleisösivu näyttää ja miten biisitoivekanava toimii.",
              })}
            </p>

            <nav
              className="settings-quick-nav mt-5 grid max-w-full grid-cols-3 gap-2 pb-1 sm:flex sm:flex-wrap"
              aria-label={t("settingsPage.quickNav.ariaLabel", { defaultValue: "Asetusten pikavalinnat" })}
            >
              <a
                href="#settings-general"
                className="flex min-w-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-2 py-2 text-center text-xs font-black text-[#541717] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10 sm:px-4"
              >
                {t("settingsPage.quickNav.general", {
                  defaultValue: "Yleisösivu",
                })}
              </a>

              <a
                href="#settings-payments"
                className="flex min-w-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-2 py-2 text-center text-xs font-black text-[#541717] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10 sm:px-4"
              >
                {t("settingsPage.quickNav.payments", {
                  defaultValue: "Maksut",
                })}
              </a>

              <a
                href="#settings-qr"
                className="flex min-w-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-2 py-2 text-center text-xs font-black text-[#541717] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10 sm:px-4"
              >
                {t("settingsPage.quickNav.qr", {
                  defaultValue: "QR-koodi",
                })}
              </a>

              <a
                href="#settings-google"
                className="flex min-w-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-2 py-2 text-center text-xs font-black text-[#541717] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10 sm:px-4"
              >
                {t("settingsPage.quickNav.google", {
                  defaultValue: "Google",
                })}
              </a>

              {canUseSocialLinks && (
                <a
                  href="#settings-links"
                  className="flex min-w-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-2 py-2 text-center text-xs font-black text-[#541717] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10 sm:px-4"
                >
                  {t("settingsPage.quickNav.links", {
                    defaultValue: "Linkit",
                  })}
                </a>
              )}

              {canUsePublicBranding && (
                <a
                  href="#settings-appearance"
                  className="flex min-w-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 px-2 py-2 text-center text-xs font-black text-[#541717] no-underline transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10 sm:px-4"
                >
                  {t("settingsPage.quickNav.appearance", {
                    defaultValue: "Ulkoasu",
                  })}
                </a>
              )}
            </nav>

            {isLoading ? (
              <p className="mt-8 text-sm font-bold text-[#6b4229] theme-dark:text-zinc-400">
                {t("settingsPage.common.loading", {
                  defaultValue: "Ladataan asetuksia...",
                })}
              </p>
            ) : (
              <div className="settings-page-sections mt-8 grid w-full max-w-full min-w-0 gap-6 sm:gap-8">
                <div className="sticky top-20 z-30 -mx-1 flex items-center justify-between gap-2 rounded-2xl border border-[#5a3824]/15 bg-[#fff8ef]/95 px-3 py-2.5 shadow-xl shadow-[#3a2b18]/10 backdrop-blur theme-dark:border-white/10 theme-dark:bg-zinc-950/95 theme-dark:shadow-black/30 sm:top-28 sm:gap-3 sm:p-3">
                  <div className="min-w-0">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a5a35] theme-dark:text-zinc-500">
                      {t("settingsPage.title", {
                        defaultValue: "Asetukset",
                      })}
                    </p>

                    {message && (
                      <p className="mt-1 text-sm font-bold text-emerald-700 theme-dark:text-emerald-300">
                        {message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSaving}
                    className="shrink-0 rounded-full bg-red-500 px-4 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-60 sm:px-6 sm:py-3 sm:text-sm sm:tracking-[0.16em]"
                  >
                    {isSaving
                      ? t("settingsPage.common.saving", {
                          defaultValue: "Tallennetaan...",
                        })
                      : t("settingsPage.common.saveSettings", {
                          defaultValue: "Tallenna asetukset",
                        })}
                  </button>
                </div>

                <section id="settings-general" className="scroll-mt-32 grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 p-4 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 sm:p-5">
                  <div>
                    <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.general.title", {
                        defaultValue: "Yleisösivu",
                      })}
                    </h2>

                    <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.general.description", {
                        defaultValue:
                          "Nämä tekstit näkyvät yleisölle Paranoidi-sivun alussa.",
                      })}
                    </p>
                  </div>

                  {renderContentLanguageSwitcher(
                    "settingsPage.contentLanguage.generalHelp",
                    "Suomen- ja englanninkieliset otsikot ja esittelytekstit tallennetaan erikseen. Jos englanninkielinen kenttä jätetään tyhjäksi, julkinen EN-sivu käyttää Paranoidin lokalisoitua oletustekstiä."
                  )}

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    {t("settingsPage.general.publicTitleLabel", {
                      defaultValue: "Yleisösivun pääotsikko",
                    })}
                    <input
                      type="text"
                      value={activePublicTitleDraft}
                      onChange={(event) =>
                        setActivePublicTitleDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none transition placeholder:text-[#8a5a35]/55 focus:border-[#ef4444]/55 focus:bg-white [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-zinc-950 theme-dark:text-white theme-dark:placeholder:text-zinc-600 theme-dark:focus:border-red-400 theme-dark:[color-scheme:dark]"
                      placeholder={t(
                        "settingsPage.general.publicTitlePlaceholder",
                        {
                          lng: contentLanguage,
                          defaultValue: isEditingEnglishContent
                            ? "E.g. Trubaduuri Seppo's song request page"
                            : "Esim. Trubaduuri Sepon toivekanava",
                        }
                      )}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    {t("settingsPage.general.publicIntroLabel", {
                      defaultValue: "Yleisösivun esittelyteksti",
                    })}
                    <textarea
                      value={activePublicIntroDraft}
                      onChange={(event) =>
                        setActivePublicIntroDraft(event.target.value)
                      }
                      maxLength={180}
                      rows={4}
                      className="w-full min-w-0 resize-none rounded-2xl border border-[#5a3824]/15 bg-white/75 px-4 py-3 text-sm font-semibold leading-6 text-[#241a16] outline-none transition placeholder:text-[#8a5a35]/55 focus:border-[#ef4444]/55 focus:bg-white [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-zinc-950 theme-dark:text-white theme-dark:placeholder:text-zinc-600 theme-dark:focus:border-red-400 theme-dark:[color-scheme:dark]"
                      placeholder={t(
                        "settingsPage.general.publicIntroPlaceholder",
                        {
                          lng: contentLanguage,
                          defaultValue: isEditingEnglishContent
                            ? "E.g. Send a song request for the gig or live show. Browse the repertoire and leave your own request."
                            : "Esim. Lähetä biisitoive keikalle tai liveen. Voit hakea kappaleita ohjelmistosta ja jättää oman toiveesi.",
                        }
                      )}
                    />

                    <span className="text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                      {t("settingsPage.common.characterCount", {
                        count: activePublicIntroDraft.length,
                        max: 180,
                        defaultValue: "{{count}}/{{max}} merkkiä",
                      })}
                    </span>
                  </label>

                  <label className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-3 transition-colors theme-dark:border-white/10 theme-dark:bg-zinc-950">
                    <span>
                      <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                        {t("settingsPage.general.requestChannelLabel", {
                          defaultValue: "Avaa biisitoivekanava yleisölle",
                        })}
                      </span>

                      <span className="mt-1 block text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                        {t("settingsPage.general.requestChannelHelp", {
                          defaultValue:
                            "Kun tämä on pois päältä, yleisö näkee sivun alun, mutta biisihaku ja biisitoiveiden lähettäminen eivät ole käytössä.",
                        })}
                      </span>
                    </span>

                    <input
                      type="checkbox"
                      checked={isLiveActiveDraft}
                      onChange={(event) =>
                        setIsLiveActiveDraft(event.target.checked)
                      }
                      className="h-5 w-5 accent-red-500"
                    />
                  </label>

                  <label className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-3 transition-colors theme-dark:border-white/10 theme-dark:bg-zinc-950">
                    <span>
                      <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                        {t("settingsPage.general.randomLabel", {
                          defaultValue:
                            "Näytä Satunnainen kappale -toiminto yleisösivulla",
                        })}
                      </span>

                      <span className="mt-1 block text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                        {t("settingsPage.general.randomHelp", {
                          defaultValue:
                            "Kun tämä on pois päältä, yleisösivu avaa suoraan biisihaun eikä näytä Satunnainen kappale -toimintoa.",
                        })}
                      </span>
                    </span>

                    <input
                      type="checkbox"
                      checked={randomEnabledDraft}
                      onChange={(event) =>
                        setRandomEnabledDraft(event.target.checked)
                      }
                      className="h-5 w-5 accent-red-500"
                    />
                  </label>

                  <label className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-3 transition-colors theme-dark:border-white/10 theme-dark:bg-zinc-950">
                    <span>
                      <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                        {t("settingsPage.general.raffleLabel", {
                          defaultValue:
                            "Käytän biisitoiveiden yhteydessä mahdollista arvontaa",
                        })}
                      </span>

                      <span className="mt-1 block text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                        {t("settingsPage.general.raffleHelp", {
                          defaultValue:
                            "Kun tämä on päällä, yleisö voi valita biisitoiveen yhteydessä osallistuvansa mahdolliseen arvontaan. Arvonta on käytettävissä vain, kun biisitoivekanava on auki yleisölle.",
                        })}
                      </span>
                    </span>

                    <input
                      type="checkbox"
                      checked={isLiveActiveDraft && requestRaffleEnabledDraft}
                      disabled={!isLiveActiveDraft}
                      onChange={(event) =>
                        setRequestRaffleEnabledDraft(event.target.checked)
                      }
                      className="h-5 w-5 accent-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </label>
                </section>

                {/* STRIPE_CONNECT_SETTINGS_UI */}
                <section id="settings-payments" className="scroll-mt-32 grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 p-4 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 sm:p-5">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#b42318] theme-dark:text-red-400">
                      {t("settingsPage.payments.eyebrow", {
                        defaultValue: "Maksut",
                      })}
                    </p>

                    <h2 className="mt-2 text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.payments.title", {
                        defaultValue: "Maksulliset biisitoiveiden nostot",
                      })}
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.payments.description", {
                        amount: paidRequestBoostAmountLabel,
                        defaultValue:
                          "Yleisö voi maksaa {{amount}} siitä, että biisitoive nostetaan näkyvästi muiden toiveiden joukossa. Maksu ei takaa kappaleen esittämistä.",
                      })}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-[#5a3824]/12 bg-[#fff8ef]/70 p-4 theme-dark:border-white/10 theme-dark:bg-zinc-950">
                    <button
                      type="button"
                      onClick={() => setShowStripeHelp((current) => !current)}
                      className="inline-flex items-center justify-center rounded-full border border-[#5a3824]/18 bg-white/70 px-5 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-[#541717] transition hover:border-[#ef4444]/35 hover:bg-white hover:text-[#b42318] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white theme-dark:hover:bg-white/10"
                    >
                      {showStripeHelp
                        ? t("settingsPage.payments.hideHelp", {
                            defaultValue: "Piilota ohje",
                          })
                        : t("settingsPage.payments.showHelp", {
                            defaultValue: "Miten tämä toimii?",
                          })}
                    </button>

                    {showStripeHelp && (
                      <div className="mt-4 grid gap-3 text-sm font-semibold leading-6 text-[#5a3824] theme-dark:text-zinc-300">
                        <p>
                          <strong>1.</strong>{" "}
                          {t("settingsPage.payments.help.step1", {
                            defaultValue: "Yhdistä Stripe Paranoidiin.",
                          })}
                        </p>

                        <p>
                          <strong>2.</strong>{" "}
                          {t("settingsPage.payments.help.step2", {
                            defaultValue:
                              "Stripe pyytää tarvittavat henkilötiedot ja tilitystilin.",
                          })}
                        </p>

                        <p>
                          <strong>3.</strong>{" "}
                          {t("settingsPage.payments.help.step3", {
                            amount: paidRequestBoostAmountLabel,
                            defaultValue:
                              "Ota {{amount}} näkyvyysnosto käyttöön.",
                          })}
                        </p>

                        <p>
                          <strong>4.</strong>{" "}
                          {t("settingsPage.payments.help.step4", {
                            defaultValue:
                              "Yleisö voi lähettää biisitoiveen normaalisti ilmaiseksi ja halutessaan ostaa sille näkyvyysnoston.",
                          })}
                        </p>

                        <p>
                          <strong>5.</strong>{" "}
                          {t("settingsPage.payments.help.step5", {
                            defaultValue:
                              "Stripe käsittelee maksun ja tilittää rahat Stripe-tilisi kautta ilmoittamallesi pankkitilille.",
                          })}
                        </p>

                        <p className="rounded-xl border border-[#5a3824]/10 bg-white/70 px-3 py-3 text-xs font-bold leading-5 text-[#6b4229] theme-dark:border-white/10 theme-dark:bg-black/30 theme-dark:text-zinc-400">
                          {t("settingsPage.payments.help.note", {
                            defaultValue:
                              "Biisitoiveen lähettäminen pysyy maksuttomana. Maksullinen nosto parantaa vain toiveen näkyvyyttä eikä takaa kappaleen esittämistä.",
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  {stripeConnectStatus.isLoading ? (
                    <div className="rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-4 text-sm font-semibold text-[#6b4229] theme-dark:border-white/10 theme-dark:bg-zinc-950 theme-dark:text-zinc-400">
                      {t("settingsPage.payments.checkingStripe", {
                        defaultValue: "Tarkistetaan Stripe-yhteyttä...",
                      })}
                    </div>
                  ) : stripeConnectStatus.error ? (
                    <div className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-4">
                      <p className="text-sm font-black text-red-700 theme-dark:text-red-200">
                        {t("settingsPage.payments.stripeCheckFailed", {
                          defaultValue: "Stripe-yhteyttä ei voitu tarkistaa.",
                        })}
                      </p>

                      <p className="mt-1 text-sm font-semibold leading-6 text-red-700/80 theme-dark:text-red-200/80">
                        {stripeConnectStatus.error}
                      </p>
                    </div>
                  ) : !stripeConnectStatus.connected ? (
                    <div className="grid gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 p-4 theme-dark:border-white/10 theme-dark:bg-zinc-950">
                      <div>
                        <p className="text-sm font-black text-[#241a16] theme-dark:text-white">
                          {t("settingsPage.payments.notConnectedTitle", {
                            defaultValue: "Stripea ei ole vielä yhdistetty",
                          })}
                        </p>

                        <p className="mt-1 text-sm font-semibold leading-6 text-[#6b4229] theme-dark:text-zinc-400">
                          {t("settingsPage.payments.notConnectedText", {
                            defaultValue:
                              "Yhdistä Stripe, jotta voit vastaanottaa näkyvyysnostojen maksut omalle maksutilillesi. Paranoidi ei ota maksusta provisiota. Stripe vähentää omat maksunkäsittelykulunsa.",
                          })}
                        </p>
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={handleStripeConnect}
                          disabled={isStartingStripeConnect}
                          className="rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isStartingStripeConnect
                            ? t("settingsPage.payments.openingStripe", {
                                defaultValue: "Avataan Stripeä...",
                              })
                            : t("settingsPage.payments.connectStripe", {
                                defaultValue: "Yhdistä Stripe",
                              })}
                        </button>
                      </div>
                    </div>
                  ) : !stripeConnectReady ? (
                    <div className="grid gap-4 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4">
                      <div>
                        <p className="text-sm font-black text-amber-900 theme-dark:text-amber-100">
                          {t("settingsPage.payments.incompleteTitle", {
                            defaultValue: "Stripe-yhteys on kesken",
                          })}
                        </p>

                        <p className="mt-1 text-sm font-semibold leading-6 text-amber-900/80 theme-dark:text-amber-100/80">
                          {t("settingsPage.payments.incompleteText", {
                            defaultValue:
                              "Viimeistele Stripen pyytämät tiedot ennen kuin maksullisen näkyvyysnoston voi ottaa käyttöön.",
                          })}
                        </p>
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={handleStripeConnect}
                          disabled={isStartingStripeConnect}
                          className="rounded-full bg-amber-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-black transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isStartingStripeConnect
                            ? t("settingsPage.payments.openingStripe", {
                                defaultValue: "Avataan Stripeä...",
                              })
                            : t("settingsPage.payments.continueStripe", {
                                defaultValue: "Jatka Stripe-käyttöönottoa",
                              })}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-4">
                        <p className="text-sm font-black text-emerald-800 theme-dark:text-emerald-200">
                          {t("settingsPage.payments.connectedTitle", {
                            defaultValue: "Stripe yhdistetty ✓",
                          })}
                        </p>

                        <p className="mt-1 text-sm font-semibold leading-6 text-emerald-800/80 theme-dark:text-emerald-200/80">
                          {t("settingsPage.payments.connectedText", {
                            defaultValue:
                              "Maksujen vastaanotto ja tilitykset ovat valmiina. Paranoidi ei ota näkyvyysnostosta provisiota.",
                          })}
                        </p>
                      </div>

                      <label className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/12 bg-white/65 px-4 py-4 transition-colors theme-dark:border-white/10 theme-dark:bg-zinc-950">
                        <span className="min-w-0">
                          <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                            {t("settingsPage.payments.enableBoostLabel", {
                              amount: paidRequestBoostAmountLabel,
                              defaultValue:
                                "Salli maksullinen {{amount}} näkyvyysnosto",
                            })}
                          </span>

                          <span className="mt-1 block text-xs font-semibold leading-5 text-[#6b4229] theme-dark:text-zinc-400">
                            {t("settingsPage.payments.enableBoostHelp", {
                              defaultValue:
                                "Yleisö maksaa biisitoiveen näkyvyysnostosta, ei kappaleen esittämisestä. Kun otat tämän pois päältä, maksullinen nosto poistuu yleisöltä mutta Stripe-yhteys säilyy valmiina.",
                            })}
                          </span>
                        </span>

                        <input
                          type="checkbox"
                          checked={paidRequestBoostsEnabledDraft}
                          onChange={(event) =>
                            setPaidRequestBoostsEnabledDraft(
                              event.target.checked
                            )
                          }
                          className="h-5 w-5 shrink-0 accent-red-500"
                        />
                      </label>

                      <p className="text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                        {t("settingsPage.payments.saveHint", {
                          defaultValue:
                            "Muutos tallennetaan sivun alareunan Tallenna asetukset -painikkeella.",
                        })}
                      </p>
                    </div>
                  )}

                  {stripeConnectMessage && (
                    <p className="text-sm font-bold text-red-600 theme-dark:text-red-300">
                      {stripeConnectMessage}
                    </p>
                  )}
                </section>

                <section id="settings-qr" className="scroll-mt-32 grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 p-4 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 sm:p-5">
                  <div>
                    <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.qr.title", {
                        defaultValue: "Oma yleisölinkki ja QR-koodi",
                      })}
                    </h2>

                    <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.qr.description", {
                        defaultValue:
                          "Jaa tämä linkki yleisölle keikalla, livessä tai QR-koodilla suoraan paikan päällä.",
                      })}
                    </p>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-[240px_1fr] lg:items-center">
                    <div className="mx-auto rounded-3xl border border-[#5a3824]/12 bg-white p-4 shadow-xl shadow-[#3a2b18]/10 theme-dark:border-white/10 theme-dark:shadow-black/40 lg:mx-0">
                      <QRCodeSVG
                        id="paranoidi-public-qr-svg"
                        value={publicChannelUrl}
                        size={200}
                        level="H"
                        includeMargin
                      />
                    </div>

                    <div className="grid min-w-0 gap-3">
                      <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                        {t("settingsPage.qr.publicLinkLabel", {
                          defaultValue: "Julkinen yleisölinkki",
                        })}
                        <input
                          type="text"
                          readOnly
                          value={publicChannelUrl}
                          className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-zinc-950 theme-dark:text-white theme-dark:[color-scheme:dark]"
                        />
                      </label>

                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        <button
                          type="button"
                          onClick={handleCopyPublicChannelUrl}
                          className="rounded-full border border-[#5a3824]/18 bg-white/70 px-6 py-3 text-sm font-black uppercase tracking-[0.16em] text-[#541717] transition hover:border-[#ef4444]/35 hover:bg-white hover:text-[#b42318] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white theme-dark:hover:bg-white/10"
                        >
                          {t("settingsPage.qr.copyLink", {
                            defaultValue: "Kopioi linkki",
                          })}
                        </button>

                        <button
                          type="button"
                          onClick={() => setShowPublicQr(true)}
                          className="rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.16em] text-white transition hover:bg-red-400"
                        >
                          {t("settingsPage.qr.showQr", {
                            defaultValue: "Näytä QR yleisölle",
                          })}
                        </button>

                        <a
                          href={publicChannelPath}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center rounded-full border border-[#5a3824]/18 bg-white/70 px-6 py-3 text-sm font-black uppercase tracking-[0.16em] text-[#541717] no-underline transition hover:border-[#ef4444]/35 hover:bg-white hover:text-[#b42318] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white theme-dark:hover:bg-white/10"
                        >
                          {t("settingsPage.qr.openPublicPage", {
                            defaultValue: "Avaa yleisösivu",
                          })}
                        </a>
                      </div>

                      <div className="grid gap-3 rounded-2xl border border-[#5a3824]/12 bg-[#fff8ef]/70 p-4 theme-dark:border-white/10 theme-dark:bg-zinc-950">
                        <div>
                          <p className="text-sm font-black text-[#241a16] theme-dark:text-white">
                            {t("settingsPage.qr.downloadTitle", {
                              defaultValue: "Tulosta tai lataa QR-koodi",
                            })}
                          </p>

                          <p className="mt-1 text-xs font-semibold leading-5 text-[#6b4229] theme-dark:text-zinc-400">
                            {t("settingsPage.qr.downloadHelp", {
                              defaultValue:
                                "Tulosta valmis A4-kyltti keikalle tai lataa QR-koodi omaa julistetta, pöytäkylttiä tai muuta materiaalia varten.",
                            })}
                          </p>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                          <button
                            type="button"
                            onClick={handlePrintQrPoster}
                            className="rounded-full bg-red-500 px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400"
                          >
                            {t("settingsPage.qr.printPoster", {
                              defaultValue: "Tulosta A4-kyltti",
                            })}
                          </button>

                          <button
                            type="button"
                            onClick={handleDownloadQrPng}
                            className="rounded-full border border-[#5a3824]/18 bg-white/70 px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-[#541717] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white theme-dark:hover:bg-white/10"
                          >
                            {t("settingsPage.qr.downloadPng", {
                              defaultValue: "Lataa QR PNG",
                            })}
                          </button>

                          <button
                            type="button"
                            onClick={handleDownloadQrSvg}
                            className="rounded-full border border-[#5a3824]/18 bg-white/70 px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-[#541717] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white theme-dark:hover:bg-white/10"
                          >
                            {t("settingsPage.qr.downloadSvg", {
                              defaultValue: "Lataa QR SVG",
                            })}
                          </button>
                        </div>
                      </div>

                      <p className="text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                        {t("settingsPage.qr.displayHint", {
                          defaultValue:
                            "Voit näyttää QR-koodin yleisölle puhelimelta, tabletilta tai screeniltä.",
                        })}
                      </p>
                    </div>
                  </div>
                </section>
                
                {canUseSeoFaq ? (
                  <section id="settings-google" className="scroll-mt-32 grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                    <div>
                       <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                        {t("settingsPage.google.title", {
                          defaultValue:
                            "Google-näkyvyys ja usein kysytyt kysymykset",
                        })}
                      </h2>

                      <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                        {t("settingsPage.google.description", {
                          defaultValue:
                            "Näillä teksteillä voit tehdä Paranoidi-sivusta paremman promosivun myös silloin, kun biisitoiveet ovat kiinni. Kysymykset voivat koskea esimerkiksi varaamista, tilaisuuksia, ohjelmistoa ja biisitoiveita.",
                        })}
                      </p>
                    </div>

                    {renderContentLanguageSwitcher(
                      "settingsPage.contentLanguage.googleHelp",
                      "SEO-tiedot ja UKK tallennetaan erikseen kielittäin. Englanninkielinen UKK näkyy EN-sivulla vasta, kun siihen on lisätty englanninkieliset kysymykset ja vastaukset."
                    )}

                    <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                      {t("settingsPage.google.seoTitleLabel", {
                        defaultValue: "Google-otsikko (SEO)",
                      })}
                      <input
                        type="text"
                        value={activeSeoTitleDraft}
                        onChange={(event) =>
                          setActiveSeoTitleDraft(event.target.value)
                        }
                        maxLength={90}
                        className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                        placeholder={t("settingsPage.google.seoTitlePlaceholder", {
                          lng: contentLanguage,
                          defaultValue: isEditingEnglishContent
                            ? "E.g. Party band for weddings, corporate parties and events"
                            : "Esim. Bilebändi häihin, yritysjuhliin ja tapahtumiin",
                        })}
                      />

                      <span className="text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                        {t("settingsPage.google.seoTitleHelp", {
                          count: activeSeoTitleDraft.length,
                          max: 90,
                          defaultValue:
                            "Tämä voi näkyä hakutuloksissa ja linkin jaossa. {{count}}/{{max}} merkkiä",
                        })}
                      </span>
                    </label>

                    <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                      {t("settingsPage.google.seoDescriptionLabel", {
                        defaultValue: "Google-kuvaus (SEO)",
                      })}
                      <textarea
                        value={activeSeoDescriptionDraft}
                        onChange={(event) =>
                          setActiveSeoDescriptionDraft(event.target.value)
                        }
                        maxLength={180}
                        rows={3}
                        className="w-full min-w-0 resize-none rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold leading-6 text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                        placeholder={t("settingsPage.google.seoDescriptionPlaceholder", {
                          lng: contentLanguage,
                          defaultValue: isEditingEnglishContent
                            ? "E.g. Live music for weddings, corporate parties and private events. Browse the repertoire and send a song request on the Paranoidi page."
                            : "Esim. Esiintyjä tarjoaa livemusiikkia häihin, yritysjuhliin ja yksityistilaisuuksiin. Tutustu ohjelmistoon ja lähetä biisitoive Paranoidi-sivulla.",
                        })}
                      />

                      <span className="text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                        {t("settingsPage.google.seoDescriptionHelp", {
                          count: activeSeoDescriptionDraft.length,
                          max: 180,
                          defaultValue:
                            "Lyhyt esittely, joka voi näkyä Googlessa ja linkin jaossa. {{count}}/{{max}} merkkiä",
                        })}
                      </span>
                    </label>

                    <div className="grid gap-3 rounded-3xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h3 className="text-lg font-black text-[#241a16] theme-dark:text-white">
                            {t("settingsPage.google.faqTitle", {
                              defaultValue: "Usein kysytyt kysymykset",
                            })}{" "}
                            <span className="align-middle text-xs font-black uppercase tracking-[0.14em] text-red-600 theme-dark:text-red-300">
                              {contentLanguage.toUpperCase()}
                            </span>
                          </h3>

                          <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                            {t("settingsPage.google.faqDescription", {
                              defaultValue:
                                "Voit kirjoittaa kysymykset itse tai lisätä valmiin pohjan ja muokata sen omalle kokoonpanolle sopivaksi. Enintään 8 kysymystä.",
                            })}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => addFaqDraft()}
                          disabled={activeFaqDrafts.length >= 8}
                          className="rounded-full border border-red-400/30 bg-red-500/10 px-6 py-3 text-xs font-black uppercase tracking-[0.14em] text-red-700 theme-dark:text-red-100 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {t("settingsPage.google.addOwnFaq", {
                            defaultValue: "Lisää oma",
                          })}
                        </button>
                      </div>

                      <div className="grid gap-2">
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#8a5a35] theme-dark:text-zinc-500">
                          {t("settingsPage.google.templatesTitle", {
                            defaultValue: "Valmiit pohjat",
                          })}
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {faqTemplateItems.map((template) => (
                            <button
                              key={template.question}
                              type="button"
                              onClick={() => addFaqDraft(template)}
                              disabled={activeFaqDrafts.length >= 8}
                              className="rounded-full border border-[#5a3824]/18 bg-white/70 theme-dark:border-white/10 theme-dark:bg-white/[0.04] px-4 py-2 text-xs font-bold text-[#241a16] theme-dark:text-zinc-200 transition hover:bg-white theme-dark:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {template.question}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid gap-4">
                        {activeFaqDrafts.length === 0 && (
                          <p className="rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black px-4 py-4 text-sm font-semibold leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                            {t("settingsPage.google.noFaq", {
                              defaultValue:
                                "Ei UKK-kysymyksiä vielä. Lisää oma kysymys tai käytä valmista pohjaa.",
                            })}
                          </p>
                        )}

                        {activeFaqDrafts.map((item, index) => (
                          <div
                            key={item.id || index}
                            className="grid gap-4 rounded-3xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black p-4"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <h4 className="text-sm font-black uppercase tracking-[0.16em] text-[#5a3824] theme-dark:text-zinc-300">
                                {t("settingsPage.google.faqItemTitle", {
                                  number: index + 1,
                                  defaultValue: "UKK {{number}}",
                                })}
                              </h4>

                              <button
                                type="button"
                                onClick={() => removeFaqDraft(index)}
                                className="rounded-full border border-red-400/30 px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-red-700 theme-dark:text-red-200 transition hover:bg-red-500/10"
                              >
                                {t("settingsPage.common.remove", {
                                  defaultValue: "Poista",
                                })}
                              </button>
                            </div>

                            <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                              {t("settingsPage.google.questionLabel", {
                                defaultValue: "Kysymys",
                              })}
                              <input
                                type="text"
                                value={item.question}
                                onChange={(event) =>
                                  updateFaqDraft(index, {
                                    question: event.target.value.slice(0, 160),
                                  })
                                }
                                maxLength={160}
                                className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                                placeholder={t("settingsPage.google.questionPlaceholder", {
                                  lng: contentLanguage,
                                  defaultValue: isEditingEnglishContent
                                    ? "E.g. Can I book the performer for a wedding?"
                                    : "Esim. Voinko varata esiintyjän häihin?",
                                })}
                              />

                              <span className="text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                                {t("settingsPage.common.characterCount", {
                                  count: String(item.question || "").length,
                                  max: 160,
                                  defaultValue: "{{count}}/{{max}} merkkiä",
                                })}
                              </span>
                            </label>

                            <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                              {t("settingsPage.google.answerLabel", {
                                defaultValue: "Vastaus",
                              })}
                              <textarea
                                value={item.answer}
                                onChange={(event) =>
                                  updateFaqDraft(index, {
                                    answer: event.target.value.slice(0, 900),
                                  })
                                }
                                maxLength={900}
                                rows={5}
                                className="w-full min-w-0 resize-none rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold leading-6 text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                                placeholder={t("settingsPage.google.answerPlaceholder", {
                                  lng: contentLanguage,
                                  defaultValue: isEditingEnglishContent
                                    ? "Write the answer in your own words."
                                    : "Kirjoita vastaus omin sanoin.",
                                })}
                              />

                              <span className="text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                                {t("settingsPage.common.characterCount", {
                                  count: String(item.answer || "").length,
                                  max: 900,
                                  defaultValue: "{{count}}/{{max}} merkkiä",
                                })}
                              </span>
                            </label>

                            <label className="flex items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3">
                              <span>
                                <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                                  {t("settingsPage.google.showFaqLabel", {
                                    defaultValue: "Näytä julkisella sivulla",
                                  })}
                                </span>

                                <span className="mt-1 block text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                                  {t("settingsPage.google.showFaqHelp", {
                                    defaultValue:
                                      "Pois päältä oleva kysymys tallentuu, mutta ei näy yleisölle.",
                                  })}
                                </span>
                              </span>

                              <input
                                type="checkbox"
                                checked={item.active !== false}
                                onChange={(event) =>
                                  updateFaqDraft(index, {
                                    active: event.target.checked,
                                  })
                                }
                                className="h-5 w-5 accent-red-500"
                              />
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                ) : (
                  <section id="settings-google" className="grid w-full max-w-full min-w-0 gap-3 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                    <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.google.title", {
                        defaultValue:
                          "Google-näkyvyys ja usein kysytyt kysymykset",
                      })}
                    </h2>

                    <p className="text-sm font-semibold leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.google.lockedText", {
                        defaultValue:
                          "Google-otsikko, Google-kuvaus ja UKK-osio kuuluvat Basic- ja Plus-paketteihin. Kokeilussa Paranoidi käyttää julkisen sivun perustietoja.",
                      })}
                    </p>
                  </section>
                )}

              {canUseSocialLinks && (
                <section id="settings-links" className="scroll-mt-32 grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                  <div>
                    <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.links.title", {
                        defaultValue: "Julkiset linkit",
                      })}
                    </h2>

                    <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.links.description", {
                        defaultValue:
                          "Lisää esiintyjän verkkosivu ja somekanavat. Julkisella sivulla näytetään vain ne linkit, joihin on annettu osoite.",
                      })}
                    </p>
                  </div>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    {t("settingsPage.links.websiteLabel", {
                      defaultValue: "Oma verkkosivu",
                    })}
                    <input
                      type="url"
                      value={websiteUrlDraft}
                      onChange={(event) =>
                        setWebsiteUrlDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.links.websitePlaceholder", { defaultValue: "https://www.esimerkki.fi" })}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    Instagram
                    <input
                      type="url"
                      value={instagramUrlDraft}
                      onChange={(event) =>
                        setInstagramUrlDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.links.instagramPlaceholder", { defaultValue: "https://www.instagram.com/kayttaja" })}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    TikTok
                    <input
                      type="url"
                      value={tiktokUrlDraft}
                      onChange={(event) =>
                        setTiktokUrlDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.links.tiktokPlaceholder", { defaultValue: "https://www.tiktok.com/@kayttaja" })}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    YouTube
                    <input
                      type="url"
                      value={youtubeUrlDraft}
                      onChange={(event) =>
                        setYoutubeUrlDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.links.youtubePlaceholder", { defaultValue: "https://www.youtube.com/@kayttaja" })}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    Facebook
                    <input
                      type="url"
                      value={facebookUrlDraft}
                      onChange={(event) =>
                        setFacebookUrlDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.links.facebookPlaceholder", { defaultValue: "https://www.facebook.com/sivu" })}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    Spotify
                    <input
                      type="url"
                      value={spotifyUrlDraft}
                      onChange={(event) =>
                        setSpotifyUrlDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder="https://open.spotify.com/artist/..."
                    />
                  </label>
                </section>
              )}

              {canUseWhatsapp && (
                <section className="grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                  <div>
                    <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.whatsapp.title", {
                        defaultValue: "WhatsApp",
                      })}
                    </h2>

                    <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.whatsapp.description", {
                        defaultValue:
                          "Lisää kelluva WhatsApp-nappi julkiselle sivulle. Numero annetaan kansainvälisessä muodossa ilman plusmerkkiä.",
                      })}
                    </p>
                  </div>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    {t("settingsPage.whatsapp.numberLabel", {
                      defaultValue: "WhatsApp-numero",
                    })}
                    <input
                      type="text"
                      value={whatsappNumberDraft}
                      onChange={(event) =>
                        setWhatsappNumberDraft(event.target.value)
                      }
                      className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.whatsapp.numberPlaceholder", { defaultValue: "Esim. 358401234567" })}
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    {t("settingsPage.whatsapp.messageLabel", {
                      defaultValue: "WhatsApp-oletusviesti",
                    })}
                    <textarea
                      value={whatsappMessageDraft}
                      onChange={(event) =>
                        setWhatsappMessageDraft(event.target.value)
                      }
                      rows={3}
                      className="w-full min-w-0 resize-none rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3 text-sm font-semibold leading-6 text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                      placeholder={t("settingsPage.whatsapp.messagePlaceholder", { defaultValue: "Esim. Moi! Haluaisin kysyä keikasta." })}
                    />

                    <span className="text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                      {t("settingsPage.whatsapp.messageHelp", {
                        defaultValue:
                          "Tämä viesti täytetään valmiiksi asiakkaan WhatsAppiin, kun hän painaa julkisen sivun WhatsApp-nappia.",
                      })}
                    </span>
                  </label>

                  <label className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-3">
                    <span>
                      <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                        {t("settingsPage.whatsapp.floatingLabel", {
                          defaultValue: "Näytä kelluva WhatsApp-nappi",
                        })}
                      </span>

                      <span className="mt-1 block text-xs font-semibold text-[#5a3824] theme-dark:text-zinc-400">
                        {t("settingsPage.whatsapp.floatingHelp", {
                          defaultValue:
                            "Nappi näkyy julkisen sivun oikeassa alakulmassa, jos numero on asetettu.",
                        })}
                      </span>
                    </span>

                    <input
                      type="checkbox"
                      checked={whatsappFloatingEnabledDraft}
                      onChange={(event) =>
                        setWhatsappFloatingEnabledDraft(event.target.checked)
                      }
                      className="h-5 w-5 accent-red-500"
                    />
                  </label>
                </section>
              )}
              {canUseRequestSponsors ? (
                <section className="grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                        {t("settingsPage.sponsors.title", {
                          defaultValue: "Sponsorit",
                        })}
                      </h2>

                      <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                        {t("settingsPage.sponsors.description", {
                          defaultValue:
                            "Lisää toivemodaliin sponsorit. Yleisölle näytetään teksti Tämän toiveen tarjoaa, logo ja tarjousteksti. Enintään 5 sponsoria.",
                        })}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addSponsorDraft}
                      disabled={sponsorsDraft.length >= 5}
                      className="rounded-full border border-red-400/30 bg-red-500/10 px-5 py-3 text-xs font-black uppercase tracking-[0.14em] text-red-700 theme-dark:text-red-100 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t("settingsPage.sponsors.add", {
                        defaultValue: "Lisää sponsori",
                      })}
                    </button>
                  </div>

                  <div className="grid gap-4">
                    {sponsorsDraft.length === 0 && (
                      <p className="rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 px-4 py-4 text-sm font-semibold leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                        {t("settingsPage.sponsors.empty", {
                          defaultValue:
                            "Ei sponsoreita vielä. Lisää sponsori, jos haluat näyttää toivemodalissa yhteistyökumppanin logon ja tarjoustekstin.",
                        })}
                      </p>
                    )}

                    {sponsorsDraft.map((sponsor, index) => (
                      <div
                        key={sponsor.id || index}
                        className="grid gap-4 rounded-3xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.22em] text-red-700 theme-dark:text-red-300">
                              {t("settingsPage.sponsors.defaultLabel", {
                                defaultValue: "Tämän toiveen tarjoaa",
                              })}
                            </p>

                            <h3 className="mt-1 text-lg font-black text-[#241a16] theme-dark:text-white">
                              {t("settingsPage.sponsors.itemTitle", {
                                number: index + 1,
                                defaultValue: "Sponsori {{number}}",
                              })}
                            </h3>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeSponsorDraft(index)}
                            className="rounded-full border border-red-400/30 px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-red-700 theme-dark:text-red-200 transition hover:bg-red-500/10"
                          >
                            {t("settingsPage.common.remove", {
                              defaultValue: "Poista",
                            })}
                          </button>
                        </div>

                        <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                          {t("settingsPage.sponsors.nameLabel", {
                            defaultValue: "Sponsorin nimi",
                          })}
                          <input
                            type="text"
                            value={sponsor.name}
                            onChange={(event) =>
                              updateSponsorDraft(index, {
                                name: event.target.value,
                              })
                            }
                            maxLength={80}
                            className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                            placeholder={t("settingsPage.sponsors.namePlaceholder", { defaultValue: "Kirjoita sponsorin nimi" })}
                          />
                        </label>

                        <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                          {t("settingsPage.sponsors.offerLabel", {
                            defaultValue: "Tarjous- tai mainosteksti",
                          })}
                          <textarea
                            value={sponsor.offerText}
                            onChange={(event) =>
                              updateSponsorDraft(index, {
                                offerText: event.target.value,
                              })
                            }
                            maxLength={220}
                            rows={3}
                            className="w-full min-w-0 resize-none rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black px-4 py-3 text-sm font-semibold leading-6 text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                            placeholder={t("settingsPage.sponsors.offerPlaceholder", { defaultValue: "Kirjoita tähän sponsorin tarjoama etu, viesti tai mainosteksti." })}
                          />

                          <span className="text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                            {t("settingsPage.common.characterCount", {
                              count: String(sponsor.offerText || "").length,
                              max: 220,
                              defaultValue: "{{count}}/{{max}} merkkiä",
                            })}
                          </span>
                        </label>

                        <label className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                          {t("settingsPage.sponsors.linkLabel", {
                            defaultValue: "Linkki, vapaaehtoinen",
                          })}
                          <input
                            type="url"
                            value={sponsor.linkUrl}
                            onChange={(event) =>
                              updateSponsorDraft(index, {
                                linkUrl: event.target.value,
                              })
                            }
                            className="w-full min-w-0 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:text-white transition focus:border-red-400"
                            placeholder="https://..."
                          />
                        </label>

                        <div className="grid gap-3 rounded-2xl border border-[#5a3824]/12 bg-white/55 theme-dark:border-white/10 theme-dark:bg-black/40 p-4">
                          <div className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                            <span>
                              {t("settingsPage.sponsors.logoLabel", {
                                defaultValue: "Logo",
                              })}
                            </span>

                            <label
                              className={[
                                "inline-flex w-fit cursor-pointer items-center rounded-full border px-4 py-2.5",
                                "text-xs font-black uppercase tracking-[0.12em] transition",
                                "border-red-300 bg-red-50 text-red-700 hover:border-red-400 hover:bg-red-100",
                                "focus-within:ring-2 focus-within:ring-red-400",
                                "theme-dark:border-red-400/30 theme-dark:bg-red-500/10 theme-dark:text-red-100",
                                "theme-dark:hover:border-red-400/50 theme-dark:hover:bg-red-500/20",
                                uploadingSponsorIndex === index
                                  ? "pointer-events-none opacity-60"
                                  : "",
                              ].join(" ")}
                            >
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={(event) =>
                                  handleUploadSponsorLogo(
                                    index,
                                    event.target.files?.[0]
                                  )
                                }
                                disabled={uploadingSponsorIndex === index}
                                className="sr-only"
                              />

                              <span>
                                {t("settingsPage.sponsors.chooseLogo", {
                                  defaultValue: "Valitse logo",
                                })}
                              </span>
                            </label>
                          </div>

                          <p className="text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                            {t("settingsPage.sponsors.logoHelp", {
                              defaultValue:
                                "Sallitut muodot: PNG, JPG ja WebP. Suurin koko 1 Mt.",
                            })}
                          </p>

                          {sponsor.logoUrl && (
                            <div className="rounded-2xl border border-white/10 bg-white p-4">
                              <img
                                src={resolveAssetUrl(sponsor.logoUrl)}
                                alt={sponsor.name || t("settingsPage.sponsors.logoAlt", { defaultValue: "Sponsorilogo" })}
                                className="mx-auto max-h-20 max-w-[220px] object-contain"
                              />
                            </div>
                          )}

                          {sponsorLogoMessages[index] && (
                            <p className="text-sm font-bold text-[#5a3824] theme-dark:text-zinc-300">
                              {sponsorLogoMessages[index]}
                            </p>
                          )}

                          {uploadingSponsorIndex === index && (
                            <p className="text-sm font-bold text-red-700 theme-dark:text-red-200">
                              {t("settingsPage.sponsors.uploadingLogo", {
                                defaultValue: "Ladataan logoa...",
                              })}
                            </p>
                          )}
                        </div>

                        <label className="flex items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black px-4 py-3">
                          <span>
                            <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                              {t("settingsPage.sponsors.activeLabel", {
                                defaultValue: "Sponsori aktiivinen",
                              })}
                            </span>

                            <span className="mt-1 block text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                              {t("settingsPage.sponsors.activeHelp", {
                                defaultValue:
                                  "Pois päältä oleva sponsori ei näy yleisölle.",
                              })}
                            </span>
                          </span>

                          <input
                            type="checkbox"
                            checked={sponsor.active !== false}
                            onChange={(event) =>
                              updateSponsorDraft(index, {
                                active: event.target.checked,
                              })
                            }
                            className="h-5 w-5 accent-red-500"
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </section>
              ) : (
              <section className="grid w-full max-w-full min-w-0 gap-3 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                  {t("settingsPage.sponsors.title", {
                    defaultValue: "Sponsorit",
                  })}
                </h2>

                <p className="text-sm font-semibold leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                  {t("settingsPage.sponsors.lockedText", {
                    defaultValue:
                      "Sponsorit biisitoiveiden yhteyteen kuuluvat Plus-pakettiin. Kokeilussa ja Basic-paketissa sponsorit eivät ole käytössä.",
                  })}
                </p>
              </section>
            )}

              {canUsePublicBranding && (
                <section id="settings-appearance" className="scroll-mt-32 grid w-full max-w-full min-w-0 gap-4 rounded-3xl border border-[#5a3824]/12 bg-white/55 transition-colors duration-300 theme-dark:border-white/10 theme-dark:bg-black/20 p-4 sm:p-5">
                  <div>
                    <h2 className="text-xl font-black text-[#241a16] theme-dark:text-white">
                      {t("settingsPage.appearance.title", {
                        defaultValue: "Ulkoasu",
                      })}
                    </h2>

                    <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                      {t("settingsPage.appearance.description", {
                        defaultValue:
                          "Lataa kuva, joka näkyy yleisösivun yläosassa. Kuva otetaan käyttöön heti latauksen jälkeen. Tekstit ja muut asetukset tallennetaan erikseen sivun alareunan “Tallenna asetukset” -painikkeella.",
                      })}
                    </p>
                  </div>

                  <div className="grid gap-2 text-sm font-bold text-[#241a16] theme-dark:text-zinc-200">
                    <span>
                      {t("settingsPage.appearance.imageLabel", {
                        defaultValue: "Valitse julkisen sivun kuva",
                      })}
                    </span>

                    <label className="flex min-w-0 cursor-pointer flex-col gap-2 rounded-2xl border border-[#5a3824]/15 bg-white/75 p-2 transition hover:border-red-300 theme-dark:border-white/10 theme-dark:bg-zinc-950 theme-dark:hover:border-red-400/35 sm:flex-row sm:items-center">
                      <input
                        key={heroImageInputKey}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;

                          if (!file) {
                            setHeroImageFile(null);
                            setUploadMessage("");
                            return;
                          }

                          if (file.size > HERO_IMAGE_MAX_BYTES) {
                            setHeroImageFile(null);
                            setUploadMessage(heroImageTooLargeMessage);
                            event.target.value = "";
                            return;
                          }

                          setHeroImageFile(file);
                          setUploadMessage("");
                        }}
                        className="sr-only"
                      />

                      <span className="shrink-0 rounded-xl border border-red-300 bg-red-50 px-4 py-2.5 text-xs font-black uppercase tracking-[0.1em] text-red-700 transition hover:border-red-400 hover:bg-red-100 theme-dark:border-red-400/30 theme-dark:bg-red-500/10 theme-dark:text-red-100 theme-dark:hover:border-red-400/50 theme-dark:hover:bg-red-500/20">
                        {t("settingsPage.appearance.chooseImage", {
                          defaultValue: "Valitse kuva",
                        })}
                      </span>

                      <span
                        className="min-w-0 flex-1 truncate px-2 text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400"
                        title={heroImageFile?.name || undefined}
                      >
                        {heroImageFile?.name ||
                          t("settingsPage.appearance.noImageSelected", {
                            defaultValue: "Ei kuvaa valittu",
                          })}
                      </span>
                    </label>
                  </div>

                  <p className="text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                    {t("settingsPage.appearance.imageHelp", {
                      defaultValue:
                        "Suositus: käytä vaakakuvaa. Suurin sallittu koko on 5 Mt. Jos kuva on suurempi, pienennä se ennen latausta.",
                    })}
                  </p>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      onClick={handleUploadHeroImage}
                      disabled={isUploadingHeroImage || !heroImageFile}
                      className="rounded-full border border-[#5a3824]/18 bg-white/70 theme-dark:border-white/10 theme-dark:bg-white/[0.04] px-6 py-3 text-sm font-black uppercase tracking-[0.16em] text-white transition hover:bg-white theme-dark:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isUploadingHeroImage
                        ? t("settingsPage.appearance.uploading", {
                            defaultValue: "Ladataan kuvaa...",
                          })
                        : t("settingsPage.appearance.uploadButton", {
                            defaultValue: "Lataa ja ota kuva käyttöön",
                          })}
                    </button>

                    {uploadMessage && (
                      <p className="text-sm font-bold text-[#5a3824] theme-dark:text-zinc-300">
                        {uploadMessage}
                      </p>
                    )}

                    {heroImageUrlDraft && (
                      <button
                        type="button"
                        onClick={handleRemoveHeroImage}
                        disabled={isUploadingHeroImage}
                        className="rounded-full border border-red-400/30 bg-red-500/10 px-6 py-3 text-sm font-black uppercase tracking-[0.16em] text-red-700 theme-dark:text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {t("settingsPage.appearance.removeImage", {
                          defaultValue: "Poista kuva",
                        })}
                      </button>
                    )}
                  </div>

                  {heroPreviewSrc && (
                    <div className="w-full max-w-full min-w-0 rounded-3xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-zinc-950 p-4">
                      <div className="w-full max-w-full overflow-hidden rounded-2xl border border-[#5a3824]/15 bg-white/75 theme-dark:border-white/10 theme-dark:bg-black">
                        <img
                          src={heroPreviewSrc}
                          alt={
                            heroImageFile
                              ? t("settingsPage.appearance.selectedPreviewAlt", {
                                  defaultValue:
                                    "Valitun hero-kuvan esikatselu",
                                })
                              : t("settingsPage.appearance.currentPreviewAlt", {
                                  defaultValue:
                                    "Käytössä olevan hero-kuvan esikatselu",
                                })
                          }
                          className="mx-auto max-h-[420px] w-full object-contain opacity-100"
                        />
                      </div>

                      <p className="mt-3 text-xs font-semibold leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                        {t("settingsPage.appearance.previewHelp", {
                          defaultValue:
                            "Esikatselu näyttää kuvan kokonaisempana. Julkisella sivulla kuva rajautuu taustakuvaksi eri kokoisilla näytöillä.",
                        })}
                      </p>
                    </div>
                  )}
                </section>
              )}

              </div>
            )}
          </form>
        </section>
      </main>

      {publicQrOverlay}
    </>
  );
}
/*
 * Admin-pyyntöjen autentikointi perustuu palvelimen HttpOnly-session-cookieen.
 * Lähetetään cookie mukana myös localhostin frontend -> backend -pyynnöissä.
 */
function fetchWithCredentials(input, init = {}) {
  return fetch(input, {
    ...init,
    credentials: "include",
  });
}
